import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container, Paper, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Chip, Alert, FormControl, InputLabel, Select, MenuItem,
    Grid, Checkbox, FormControlLabel, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Card,
    CardContent, Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
    Event as EventIcon,
    Clear as ClearIcon,
    History as HistoryIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import api from '../services/api';

const ITEMS_PER_PAGE = 50;

function Prazos() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const [prazos, setPrazos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Estados para modal de andamentos
    const [openAndamentosModal, setOpenAndamentosModal] = useState(false);
    const [andamentos, setAndamentos] = useState([]);
    const [loadingAndamentos, setLoadingAndamentos] = useState(false);
    const [processoSelecionado, setProcessoSelecionado] = useState(null);
    
    // Filtros
    const [mostrarInativos, setMostrarInativos] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroPrioridade, setFiltroPrioridade] = useState('');
    const [filtroResponsavel, setFiltroResponsavel] = useState('');
    const [filtroVencidos, setFiltroVencidos] = useState(false);
    const [filtroVencendoHoje, setFiltroVencendoHoje] = useState(false);
    const [filtroVenceAmanha, setFiltroVenceAmanha] = useState(false);
    const [busca, setBusca] = useState('');
    
    // Filtro de data por período - padrão: mês corrente até fim do próximo mês
    const getPeriodoPadrao = () => {
        const now = new Date();
        const primeiroDiaMesAtual = new Date(now.getFullYear(), now.getMonth(), 1);
        const ultimoDiaProximoMes = new Date(now.getFullYear(), now.getMonth() + 2, 0); // +2 meses, dia 0 = último dia do mês anterior
        // Usa data local (não UTC) para evitar problema de timezone
        const formatarDataLocal = (data) => {
            const ano = data.getFullYear();
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const dia = String(data.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        };
        return {
            inicio: formatarDataLocal(primeiroDiaMesAtual),
            fim: formatarDataLocal(ultimoDiaProximoMes)
        };
    };
    const periodoPadrao = getPeriodoPadrao();
    const [filtroDataInicio, setFiltroDataInicio] = useState(periodoPadrao.inicio);
    const [filtroDataFim, setFiltroDataFim] = useState(periodoPadrao.fim);

    // Função para verificar se um filtro está ativo (diferente do padrão)
    const isFiltroAtivo = (valor, valorPadrao) => {
        if (valor === null || valor === undefined) return false;
        if (typeof valor === 'boolean') return valor !== valorPadrao;
        if (typeof valor === 'string') {
            if (valorPadrao === null || valorPadrao === undefined) return valor !== '';
            return valor !== valorPadrao;
        }
        return valor !== valorPadrao;
    };

    // Estilo para campos de filtro ativos
    const getFiltroAtivoStyle = (valor, valorPadrao) => {
        if (isFiltroAtivo(valor, valorPadrao)) {
            if (theme.palette.mode === 'dark') {
                // Modo escuro: amarelo mais escuro/opaco
                return {
                    backgroundColor: 'rgba(255, 235, 59, 0.15)', // Amarelo opaco
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 235, 59, 0.15)',
                        '& fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.3)',
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255, 235, 59, 0.7)',
                        },
                    },
                    '& .MuiInputBase-root': {
                        backgroundColor: 'rgba(255, 235, 59, 0.15)',
                        color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255, 235, 59, 0.8)',
                    },
                    '& .MuiInputBase-input': {
                        color: theme.palette.text.primary,
                    },
                };
            } else {
                // Modo claro: amarelo fraco
                return {
                    backgroundColor: '#fff9c4',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff9c4',
                    },
                    '& .MuiInputBase-root': {
                        backgroundColor: '#fff9c4',
                    }
                };
            }
        }
        return {};
    };
    
    // Dados para filtros
    const [usuarios, setUsuarios] = useState([]);
    const [partesExternas, setPartesExternas] = useState([]);
    
    // Dialog de confirmação
    const [openDialog, setOpenDialog] = useState(false);
    const [acaoDialog, setAcaoDialog] = useState(null);
    const [prazoSelecionado, setPrazoSelecionado] = useState(null);

    // Chave para localStorage
    const FILTROS_STORAGE_KEY = 'prazos_filtros';
    const SCROLL_POSITION_KEY = 'prazos_scroll_position';
    const [filtrosCarregados, setFiltrosCarregados] = useState(false);

    // Função para salvar filtros no localStorage
    const salvarFiltros = () => {
        const filtros = {
            mostrarInativos,
            filtroStatus,
            filtroPrioridade,
            filtroResponsavel,
            filtroVencidos,
            filtroVencendoHoje,
            filtroVenceAmanha,
            busca,
            filtroDataInicio,
            filtroDataFim
        };
        localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
    };

    // Carrega filtros salvos ao montar o componente (apenas uma vez)
    useEffect(() => {
        try {
            const filtrosSalvos = localStorage.getItem(FILTROS_STORAGE_KEY);
            if (filtrosSalvos) {
                const filtros = JSON.parse(filtrosSalvos);
                setMostrarInativos(filtros.mostrarInativos || false);
                setFiltroStatus(filtros.filtroStatus || '');
                setFiltroPrioridade(filtros.filtroPrioridade || '');
                setFiltroResponsavel(filtros.filtroResponsavel || '');
                setFiltroVencidos(filtros.filtroVencidos || false);
                setFiltroVencendoHoje(filtros.filtroVencendoHoje || false);
                setFiltroVenceAmanha(filtros.filtroVenceAmanha || false);
                setBusca(filtros.busca || '');
                setFiltroDataInicio(filtros.filtroDataInicio || periodoPadrao.inicio);
                setFiltroDataFim(filtros.filtroDataFim || periodoPadrao.fim);
            }
        } catch (error) {
            console.error('Erro ao carregar filtros salvos:', error);
        }
        setFiltrosCarregados(true);
        loadUsuarios();
        loadPartesExternas();
    }, []); // Executa apenas uma vez ao montar

    // Salva filtros sempre que mudarem (após terem sido carregados)
    useEffect(() => {
        if (filtrosCarregados) {
            const filtros = {
                mostrarInativos,
                filtroStatus,
                filtroPrioridade,
                filtroResponsavel,
                filtroVencidos,
                filtroVencendoHoje,
                filtroVenceAmanha,
                busca,
                filtroDataInicio,
                filtroDataFim
            };
            localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
        }
    }, [mostrarInativos, filtroStatus, filtroPrioridade, filtroResponsavel, filtroVencidos, filtroVencendoHoje, filtroVenceAmanha, busca, filtroDataInicio, filtroDataFim, filtrosCarregados]);

    // Carrega prazos quando os filtros mudarem (após terem sido carregados)
    useEffect(() => {
        if (filtrosCarregados) {
            loadPrazos();
        }
    }, [mostrarInativos, filtroStatus, filtroPrioridade, filtroResponsavel, filtroVencidos, filtroVencendoHoje, filtroVenceAmanha, busca, filtroDataInicio, filtroDataFim, filtrosCarregados]);

    // Restaura posição do scroll quando voltar da edição
    useEffect(() => {
        // Verifica se há uma posição salva
        const scrollPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
        if (scrollPosition && !loading && prazos.length > 0) {
            // Aguarda um pouco para garantir que os cards foram renderizados
            const timer = setTimeout(() => {
                window.scrollTo({
                    top: parseInt(scrollPosition, 10),
                    behavior: 'smooth'
                });
                sessionStorage.removeItem(SCROLL_POSITION_KEY); // Remove após restaurar
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [loading, prazos.length]);

    // Salva posição do scroll antes de navegar para editar
    const handleEditClick = (prazoId) => {
        // Salva a posição atual do scroll
        sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
        navigate(`/prazos/editar/${prazoId}`);
    };

    // Função para abrir modal de andamentos
    const handleVisualizarAndamentos = async (prazo) => {
        if (!prazo.processo_id) {
            setError('Este prazo não está vinculado a um processo.');
            return;
        }
        
        setProcessoSelecionado({
            id: prazo.processo_id,
            numero_processo: prazo.numero_processo,
            cliente_nome: prazo.cliente_nome
        });
        setOpenAndamentosModal(true);
        setLoadingAndamentos(true);
        setAndamentos([]);
        
        try {
            const response = await api.get(`/andamentos?processo_id=${prazo.processo_id}`);
            setAndamentos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar andamentos:', error);
            setError('Erro ao carregar andamentos do processo.');
        } finally {
            setLoadingAndamentos(false);
        }
    };

    // Função para fechar modal de andamentos
    const handleFecharAndamentosModal = () => {
        setOpenAndamentosModal(false);
        setAndamentos([]);
        setProcessoSelecionado(null);
    };

    // Função para navegar para gerenciamento de andamentos
    const handleIrParaGerenciamentoAndamentos = () => {
        if (processoSelecionado && processoSelecionado.id) {
            navigate(`/processos/${processoSelecionado.id}/andamentos`);
        }
    };

    const loadPrazos = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            
            // Por padrão, mostra apenas pendentes (exceto se filtro de vencidos estiver ativo ou se houver filtro de status)
            // IMPORTANTE: Se mostrarInativos estiver marcado, não aplica filtro de status padrão para mostrar todos
            // IMPORTANTE: Se filtroVencendoHoje ou filtroVenceAmanha estiverem ativos, não aplica filtro padrão de status
            if (filtroStatus) {
                params.append('status', filtroStatus);
                // Se o status é CONCLUIDO, não filtra por ativo (mostra todos, pois concluídos geralmente têm ativo=0)
                if (filtroStatus !== 'CONCLUIDO' && !mostrarInativos) {
                    params.append('ativo', 'true');
                }
            } else if (filtroVencendoHoje || filtroVenceAmanha) {
                // Se está filtrando por "Vencendo Hoje" ou "Vence Amanhã", não aplica filtro padrão de status
                // Não aplica filtro de ativo para mostrar todos os prazos que vencem hoje/amanhã, independente de status
                // (permite ver prazos concluídos, cancelados, etc. que vencem na data específica)
            } else if (!filtroVencidos && !mostrarInativos) {
                // Se não há filtro de status e não está filtrando vencidos E mostrarInativos não está marcado,
                // mostra apenas pendentes por padrão
                params.append('status', 'PENDENTE');
                params.append('ativo', 'true');
            } else if (!filtroVencidos && mostrarInativos) {
                // Se mostrarInativos está marcado, não aplica filtro de status (mostra todos)
                // Mas ainda pode aplicar filtro de ativo se necessário
                // Não aplica nenhum filtro de status para mostrar todos (CONCLUIDO, PENDENTE, etc)
            } else {
                // Se está filtrando vencidos, aplica filtro de ativo normalmente
                if (!mostrarInativos) {
                    params.append('ativo', 'true');
                }
            }
            
            if (filtroPrioridade) {
                params.append('prioridade', filtroPrioridade);
            }
            
            // Enviar filtro de responsável para o backend
            if (filtroResponsavel) {
                const responsavelId = parseInt(filtroResponsavel);
                // Usa prefixo no valor para identificar se é interno ou externo
                // Formato: "interno-{id}" ou "externo-{id}"
                if (filtroResponsavel.startsWith('interno-')) {
                    const id = parseInt(filtroResponsavel.replace('interno-', ''));
                    params.append('responsavel_interno_id', id);
                } else if (filtroResponsavel.startsWith('externo-')) {
                    const id = parseInt(filtroResponsavel.replace('externo-', ''));
                    params.append('responsavel_externo_id', id);
                } else {
                    // Fallback: verifica se é interno ou externo baseado na lista
                    const isInterno = usuarios.some(u => u.id === responsavelId);
                    if (isInterno) {
                        params.append('responsavel_interno_id', responsavelId);
                    } else {
                        const isExterno = partesExternas.some(pe => pe.id === responsavelId);
                        if (isExterno) {
                            params.append('responsavel_externo_id', responsavelId);
                        }
                    }
                }
            }
            
            if (filtroVencidos) {
                params.append('vencidos', 'true');
            }
            
            if (filtroVencendoHoje) {
                params.append('vencendo_hoje', 'true');
            }
            
            if (filtroVenceAmanha) {
                params.append('vence_amanha', 'true');
            }
            
            // Enviar busca para o backend
            if (busca && busca.trim()) {
                params.append('search', busca.trim());
            }
            
            // Enviar filtro de data por período
            // IMPORTANTE: Se filtroVencendoHoje ou filtroVenceAmanha estiverem ativos, não aplica filtro de data
            // para não limitar os resultados (esses filtros já filtram por data específica)
            if (!filtroVencendoHoje && !filtroVenceAmanha) {
                if (filtroDataInicio) {
                    params.append('data_inicio', filtroDataInicio);
                }
                if (filtroDataFim) {
                    params.append('data_fim', filtroDataFim);
                }
            }
            
            const response = await api.get(`/prazos?${params.toString()}`);
            
            setPrazos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar prazos:', error);
            setError('Erro ao carregar prazos: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };


    const loadUsuarios = async () => {
        try {
            const response = await api.get('/usuarios/lista');
            const usuariosData = response.data || [];
            console.log('Usuários carregados:', usuariosData);
            setUsuarios(usuariosData);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const loadPartesExternas = async () => {
        try {
            const response = await api.get('/parte-externa?ativo=true');
            setPartesExternas(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar partes externas:', error);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // Converte para string primeiro
            const dateStr = String(date).trim();
            
            // Se já está em formato brasileiro (DD/MM/YYYY), retorna como está
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
                return dateStr;
            }
            
            // Tenta formato YYYY-MM-DD (formato do SQLite - cadastros novos)
            // Pode vir como "2025-09-12" ou "2025-09-12 00:00:00" ou "2025-09-12T00:00:00.000Z"
            const matchYYYYMMDD = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (matchYYYYMMDD) {
                const [, ano, mes, dia] = matchYYYYMMDD;
                // Retorna DD/MM/YYYY
                return `${dia}/${mes}/${ano}`;
            }
            
            // Tenta formato MM/DD/YYYY (formato americano - dados da migração)
            // Exemplo: "09/12/2025" (12 de setembro) -> deve virar "12/09/2025"
            const matchMMDDYYYY = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (matchMMDDYYYY) {
                const [, mes, dia, ano] = matchMMDDYYYY;
                // Retorna DD/MM/YYYY (inverte mes e dia)
                return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
            }
            
            // Se não conseguiu parsear, retorna o valor original
            return dateStr || '-';
        } catch (error) {
            console.error('Erro ao formatar data:', date, error);
            return String(date) || '-';
        }
    };

    // Função para formatar data e hora (DD/MM/YYYY - HH:mm)
    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return { data: '-', hora: '-' };
        try {
            // Formato esperado: "2026-01-07 12:17:00" ou "2026-01-07T12:17:00.000Z"
            const dateTime = String(dateTimeStr).trim();
            
            // Remove timezone se houver e extrai data e hora
            let datePart = '';
            let timePart = '';
            
            if (dateTime.includes('T')) {
                // Formato ISO: "2026-01-07T12:17:00.000Z"
                const parts = dateTime.split('T');
                datePart = parts[0];
                timePart = parts[1]?.split('.')[0] || '';
            } else if (dateTime.includes(' ')) {
                // Formato: "2026-01-07 12:17:00"
                const parts = dateTime.split(' ');
                datePart = parts[0];
                timePart = parts[1] || '';
            } else {
                // Apenas data
                datePart = dateTime;
            }
            
            // Formata data
            const matchDate = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (matchDate) {
                const [, ano, mes, dia] = matchDate;
                const dataFormatada = `${dia}/${mes}/${ano}`;
                
                // Formata hora
                const matchTime = timePart.match(/^(\d{2}):(\d{2}):(\d{2})/);
                if (matchTime) {
                    const [, hora, minuto] = matchTime;
                    return {
                        data: dataFormatada,
                        hora: `${hora}:${minuto}`
                    };
                }
                
                return {
                    data: dataFormatada,
                    hora: '-'
                };
            }
            
            return { data: '-', hora: '-' };
        } catch (error) {
            return { data: '-', hora: '-' };
        }
    };

    const isDataFutura = (dataVencimento) => {
        if (!dataVencimento) return false;
        try {
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            // Parse manual da data para evitar problemas de timezone
            const dateStr = String(dataVencimento).trim();
            let vencimento;
            
            // Tenta formato YYYY-MM-DD (formato do SQLite)
            const matchYYYYMMDD = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (matchYYYYMMDD) {
                const [, ano, mes, dia] = matchYYYYMMDD;
                // Cria data local (evita problemas de timezone)
                vencimento = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
            } else {
                // Fallback: tenta usar new Date normalmente
                vencimento = new Date(dataVencimento);
            }
            
            // Verifica se a data é válida
            if (isNaN(vencimento.getTime())) {
                return false;
            }
            
            vencimento.setHours(0, 0, 0, 0);
            return vencimento > hoje;
        } catch {
            return false;
        }
    };

    const getStatusColor = (prazo) => {
        if (prazo.ativo === 0) return 'default';
        if (prazo.status === 'CONCLUIDO') return 'success';
        if (prazo.status === 'CANCELADO') return 'default';
        
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const vencimento = new Date(prazo.data_vencimento);
        vencimento.setHours(0, 0, 0, 0);
        
        const diffDays = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) return 'error'; // Vencido
        if (diffDays === 0) return 'warning'; // Vence hoje
        if (diffDays <= 3) return 'warning'; // Vence em 1-3 dias
        return 'primary'; // Vence em 4+ dias
    };

    const getStatusLabel = (prazo) => {
        if (prazo.ativo === 0) return 'Inativo';
        if (prazo.status === 'CONCLUIDO') return 'Concluído';
        if (prazo.status === 'CANCELADO') return 'Cancelado';
        
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const vencimento = new Date(prazo.data_vencimento);
        vencimento.setHours(0, 0, 0, 0);
        
        const diffDays = Math.floor((vencimento - hoje) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) return 'Vencido';
        if (diffDays === 0) return 'Vence Hoje';
        if (diffDays === 1) return 'Vence Amanhã';
        if (diffDays <= 3) return `Vence em ${diffDays} dias`;
        return 'Pendente';
    };

    const handleConcluir = async (prazo) => {
        try {
            await api.put(`/prazos/${prazo.id}/concluir`, {
                data_conclusao: (() => {
                    const hoje = new Date();
                    const ano = hoje.getFullYear();
                    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
                    const dia = String(hoje.getDate()).padStart(2, '0');
                    return `${ano}-${mes}-${dia}`;
                })(),
                resultado: 'Concluído pelo usuário'
            });
            loadPrazos();
        } catch (error) {
            console.error('Erro ao concluir prazo:', error);
            alert('Erro ao concluir prazo: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleMarcarInativo = async (prazo) => {
        setAcaoDialog('inativo');
        setPrazoSelecionado(prazo);
        setOpenDialog(true);
    };

    const handleDelete = async (prazo) => {
        setAcaoDialog('delete');
        setPrazoSelecionado(prazo);
        setOpenDialog(true);
    };

    const confirmarAcao = async () => {
        try {
            if (acaoDialog === 'inativo') {
                await api.put(`/prazos/${prazoSelecionado.id}/marcar-inativo`, {
                    motivo_finalizacao: 'Marcado como inativo pelo usuário'
                });
            } else if (acaoDialog === 'delete') {
                await api.delete(`/prazos/${prazoSelecionado.id}`);
            }
            setOpenDialog(false);
            setPrazoSelecionado(null);
            setAcaoDialog(null);
            loadPrazos();
        } catch (error) {
            console.error('Erro ao executar ação:', error);
            alert('Erro: ' + (error.response?.data?.error || error.message));
        }
    };

    return (
        <Container maxWidth="xl">
            {/* Header destacado */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2,
                    mt: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <EventIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Prazos Judiciais
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Gerencie todos os prazos e tarefas dos processos
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Filtros */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Filtros</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pesquisar"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                            sx={getFiltroAtivoStyle(busca, '')}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            placeholder="Processo, Cliente, Vara, Instruções..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small" sx={getFiltroAtivoStyle(filtroStatus, '')}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filtroStatus}
                                onChange={(e) => setFiltroStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="PENDENTE">Pendente</MenuItem>
                                <MenuItem value="CONCLUIDO">Concluído</MenuItem>
                                <MenuItem value="CANCELADO">Cancelado</MenuItem>
                                <MenuItem value="VENCIDO">Vencido</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small" sx={getFiltroAtivoStyle(filtroPrioridade, '')}>
                            <InputLabel>Prioridade</InputLabel>
                            <Select
                                value={filtroPrioridade}
                                onChange={(e) => setFiltroPrioridade(e.target.value)}
                                label="Prioridade"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="BAIXA">Baixa</MenuItem>
                                <MenuItem value="NORMAL">Normal</MenuItem>
                                <MenuItem value="ALTA">Alta</MenuItem>
                                <MenuItem value="URGENTE">Urgente</MenuItem>
                                <MenuItem value="FATAL">Fatal</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={getFiltroAtivoStyle(filtroResponsavel, '')}>
                            <InputLabel>Responsável</InputLabel>
                            <Select
                                value={filtroResponsavel}
                                onChange={(e) => {
                                    console.log('Responsável selecionado:', e.target.value, 'Tipo:', typeof e.target.value);
                                    setFiltroResponsavel(e.target.value);
                                }}
                                label="Responsável"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem disabled>--- Internos ---</MenuItem>
                                {usuarios.map((u) => {
                                    const nomeExibicao = (u.nome && u.nome.trim() !== '') ? u.nome : u.username;
                                    const displayText = u.nome && u.nome.trim() !== '' && u.username 
                                        ? `${u.nome} (${u.username})` 
                                        : nomeExibicao;
                                    return (
                                        <MenuItem key={`interno-${u.id}`} value={`interno-${u.id}`}>
                                            {displayText}
                                        </MenuItem>
                                    );
                                })}
                                <MenuItem disabled>--- Externos ---</MenuItem>
                                {partesExternas.map((pe) => (
                                    <MenuItem key={`externo-${pe.id}`} value={`externo-${pe.id}`}>
                                        {pe.nome_completo || pe.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Data Início"
                            type="date"
                            value={filtroDataInicio}
                            onChange={(e) => setFiltroDataInicio(e.target.value)}
                            sx={getFiltroAtivoStyle(filtroDataInicio, periodoPadrao.inicio)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Data Fim"
                            type="date"
                            value={filtroDataFim}
                            onChange={(e) => setFiltroDataFim(e.target.value)}
                            sx={getFiltroAtivoStyle(filtroDataFim, periodoPadrao.fim)}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtroVencidos}
                                        onChange={(e) => setFiltroVencidos(e.target.checked)}
                                    />
                                }
                                label="Apenas Vencidos"
                                sx={isFiltroAtivo(filtroVencidos, false) ? {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.15)' 
                                        : '#fff9c4',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    color: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.9)' 
                                        : theme.palette.text.primary
                                } : {}}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtroVencendoHoje}
                                        onChange={(e) => setFiltroVencendoHoje(e.target.checked)}
                                    />
                                }
                                label="Vencendo Hoje"
                                sx={isFiltroAtivo(filtroVencendoHoje, false) ? {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.15)' 
                                        : '#fff9c4',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    color: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.9)' 
                                        : theme.palette.text.primary
                                } : {}}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtroVenceAmanha}
                                        onChange={(e) => setFiltroVenceAmanha(e.target.checked)}
                                    />
                                }
                                label="Vence Amanhã"
                                sx={isFiltroAtivo(filtroVenceAmanha, false) ? {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.15)' 
                                        : '#fff9c4',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    color: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.9)' 
                                        : theme.palette.text.primary
                                } : {}}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={mostrarInativos}
                                        onChange={(e) => setMostrarInativos(e.target.checked)}
                                    />
                                }
                                label="Mostrar Finalizados/Inativos"
                                sx={isFiltroAtivo(mostrarInativos, false) ? {
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.15)' 
                                        : '#fff9c4',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    color: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 235, 59, 0.9)' 
                                        : theme.palette.text.primary
                                } : {}}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={() => {
                                    setBusca('');
                                    setFiltroStatus('');
                                    setFiltroPrioridade('');
                                    setFiltroResponsavel('');
                                    setFiltroVencidos(false);
                                    setFiltroVencendoHoje(false);
                                    setFiltroVenceAmanha(false);
                                    setMostrarInativos(false);
                                    const periodoAtual = getPeriodoPadrao();
                                    setFiltroDataInicio(periodoAtual.inicio);
                                    setFiltroDataFim(periodoAtual.fim);
                                }}
                                size="small"
                            >
                                Limpar Filtros
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Botão Novo Prazo */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/prazos/novo')}
                >
                    Novo Prazo
                </Button>
            </Box>

            {/* Cards */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : prazos.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Nenhum prazo encontrado
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {prazos.map((prazo) => {
                        const dataFutura = isDataFutura(prazo.data_vencimento) && prazo.ativo === 1 && prazo.status === 'PENDENTE';
                        const temMotivoFinalizacao = prazo.motivo_finalizacao && prazo.motivo_finalizacao.trim() !== '';
                        
                        // Verifica se é processo nulo
                        const isProcessoNulo = prazo.numero_processo && (
                            prazo.numero_processo.includes('99999999999') || 
                            prazo.numero_processo.includes('SEM PROCESSO') ||
                            prazo.numero_processo.includes('NULO')
                        );
                        
                        // Para processo nulo, concatena observacoes + motivo_finalizacao
                        const observacoesCompletas = isProcessoNulo
                            ? (temMotivoFinalizacao
                                ? `${prazo.observacoes || ''}${prazo.observacoes ? ' - ' : ''}${prazo.motivo_finalizacao}`
                                : prazo.observacoes || '')
                            : null;
                        
                        return (
                            <Grid item xs={12} key={prazo.id}>
                                <Card
                                    sx={{
                                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                                            ? '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)'
                                            : '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
                                        borderRadius: 3,
                                        backgroundColor: dataFutura 
                                            ? (theme) => theme.palette.mode === 'dark' ? '#3d3a1f' : '#fff9c4'
                                            : 'background.paper',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                                ? '0 4px 12px rgba(0,0,0,0.4)'
                                                : '0 4px 12px rgba(0,0,0,0.15)'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        {/* Header do Card com Data à Direita */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                            <Box sx={{ flex: 1, pr: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                                        {prazo.cliente_nome || (observacoesCompletas ? observacoesCompletas.split('\n')[0] : (prazo.observacoes ? prazo.observacoes.split('\n')[0] : 'Sem nome'))}
                                                    </Typography>
                                                    <Chip
                                                        label={
                                                            prazo.status === 'CONCLUIDO' ? 'Concluído' :
                                                            prazo.status === 'VENCIDO' ? 'Vencido' :
                                                            'Pendente'
                                                        }
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            height: '22px'
                                                        }}
                                                        color={
                                                            prazo.status === 'CONCLUIDO' ? 'success' :
                                                            prazo.status === 'VENCIDO' ? 'error' :
                                                            'default'
                                                        }
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    <strong>Processo:</strong> {prazo.numero_processo || '-'}
                                                </Typography>
                                                
                                                {/* Instruções com mais espaço */}
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                        Instruções
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                        {prazo.instrucoes || '-'}
                                                    </Typography>
                                                </Box>
                                                
                                                {/* Vara e Responsáveis */}
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                                                    {prazo.vara && (
                                                        <Chip
                                                            label={prazo.vara}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {prazo.responsavel_interno_nome && (
                                                        <Chip
                                                            label={`${prazo.responsavel_interno_nome} (Interno)`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {prazo.responsavel_externo_nome && (
                                                        <Chip
                                                            label={`${prazo.responsavel_externo_nome} (Externo)`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <Chip
                                                        label={prazo.prioridade || 'NORMAL'}
                                                        size="small"
                                                        color={
                                                            prazo.prioridade === 'FATAL' ? 'error' :
                                                            prazo.prioridade === 'URGENTE' ? 'error' :
                                                            prazo.prioridade === 'ALTA' ? 'warning' :
                                                            prazo.prioridade === 'BAIXA' ? 'default' : 'primary'
                                                        }
                                                    />
                                                </Box>
                                                
                                                {/* Botão Visualizar Andamentos */}
                                                {prazo.processo_id && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        startIcon={<HistoryIcon />}
                                                        onClick={() => handleVisualizarAndamentos(prazo)}
                                                        sx={{ 
                                                            mt: 1,
                                                            borderWidth: 2,
                                                            '&:hover': {
                                                                borderWidth: 2
                                                            }
                                                        }}
                                                    >
                                                        Visualizar Andamentos
                                                    </Button>
                                                )}
                                            </Box>
                                            
                                            {/* Data à Direita - Maior */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 120 }}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(prazo.id)}
                                                        color="primary"
                                                        title="Editar"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    {prazo.status === 'PENDENTE' && prazo.ativo === 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleConcluir(prazo)}
                                                            color="success"
                                                            title="Concluir"
                                                        >
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    {prazo.ativo === 1 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleMarcarInativo(prazo)}
                                                            color="warning"
                                                            title="Marcar como Inativo"
                                                        >
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(prazo)}
                                                        color="error"
                                                        title="Excluir"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{ textAlign: 'right', mt: 1 }}>
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Prazo
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                                        <EventIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                                            {formatDate(prazo.data_vencimento)}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    {/* Informações de Criação/Edição */}
                                                    {(prazo.ultima_movimentacao_timestamp || prazo.created_at) && (
                                                        <Box sx={{ mt: 2, textAlign: 'right' }}>
                                                            {/* Última Movimentação (se editado) */}
                                                            {prazo.ultima_movimentacao_timestamp && (
                                                                <>
                                                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                        Última movimentação
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, mb: 1.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <EventIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                                                {formatDateTime(prazo.ultima_movimentacao_timestamp).data} - {formatDateTime(prazo.ultima_movimentacao_timestamp).hora}
                                                                            </Typography>
                                                                        </Box>
                                                                        {prazo.editado_por_nome && (
                                                                            <Chip
                                                                                label={prazo.editado_por_nome}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </>
                                                            )}
                                                            
                                                            {/* Criação (sempre mostra) */}
                                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                Criado
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <EventIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                                        {prazo.created_at ? (
                                                                            formatDateTime(prazo.created_at).data + (formatDateTime(prazo.created_at).hora !== '-' ? ' - ' + formatDateTime(prazo.created_at).hora : '')
                                                                        ) : formatDate(prazo.data_criacao || '-')}
                                                                    </Typography>
                                                                </Box>
                                                                {prazo.criado_por_nome && (
                                                                    <Chip
                                                                        label={prazo.criado_por_nome}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Observações (sempre mostra para processo nulo, ou quando houver motivo_finalizacao) */}
                                        {isProcessoNulo || (temMotivoFinalizacao && !isProcessoNulo) ? (
                                            <>
                                                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                <Box>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            color: 'text.secondary',
                                                            display: 'block',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Observações
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: 'text.primary',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word'
                                                        }}
                                                    >
                                                        {isProcessoNulo 
                                                            ? observacoesCompletas
                                                            : (prazo.observacoes 
                                                                ? `${prazo.observacoes} - ${prazo.motivo_finalizacao}`
                                                                : prazo.motivo_finalizacao
                                                            )
                                                        }
                                                    </Typography>
                                                </Box>
                                            </>
                                        ) : null}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Modal de Andamentos */}
            <Dialog
                open={openAndamentosModal}
                onClose={handleFecharAndamentosModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">
                                Andamentos do Processo
                            </Typography>
                            {processoSelecionado && (
                                <Typography variant="body2" color="text.secondary">
                                    {processoSelecionado.numero_processo}
                                    {processoSelecionado.cliente_nome && ` - ${processoSelecionado.cliente_nome}`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loadingAndamentos ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : andamentos.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                            Nenhum andamento encontrado para este processo.
                        </Typography>
                    ) : (
                        <Box>
                            {andamentos.map((andamento, index) => (
                                <Card key={andamento.id || index} sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {formatDate(andamento.data_andamento)}
                                            </Typography>
                                            {andamento.tipo_andamento && (
                                                <Chip
                                                    label={andamento.tipo_andamento}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {andamento.descricao || '-'}
                                        </Typography>
                                        {andamento.visivel_cliente !== undefined && (
                                            <Chip
                                                label={andamento.visivel_cliente ? 'Visível ao Cliente' : 'Não Visível ao Cliente'}
                                                size="small"
                                                color={andamento.visivel_cliente ? 'success' : 'default'}
                                                sx={{ mt: 1 }}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button onClick={handleFecharAndamentosModal}>
                        Fechar
                    </Button>
                    {processoSelecionado && processoSelecionado.id && (
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleIrParaGerenciamentoAndamentos}
                        >
                            Gerenciar Andamentos
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmação */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>
                    {acaoDialog === 'inativo' ? 'Marcar como Inativo' : 'Cancelar Prazo'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {acaoDialog === 'inativo' 
                            ? 'Tem certeza que deseja marcar este prazo como inativo?'
                            : 'Tem certeza que deseja cancelar este prazo?'
                        }
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={confirmarAcao} variant="contained" color={acaoDialog === 'delete' ? 'error' : 'warning'}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Prazos;

